import { useHealthblocksSWR, useSubjectMember } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { AnimatePresence } from 'framer-motion'
import { Suspense, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { NativePlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'
import { mixpanel } from '@/mixpanel'
import { AppUpdateCard } from '@/modules/app/components/app-update-card/AppUpdateCard'
import { NotificationsCard } from '@/modules/app/components/notifications-card/NotificationsCard'
import { CareQuestionMobileActionButton } from '@/modules/care-question/components/care-question-action-button/mobile/CareQuestionMobileActionButton'
import { useFirstLogin } from '@/modules/profile/state/useFirstLogin'
import { HospitalIndicator } from '@/modules/shared/components/hospital-indicator/HospitalIndicator'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { usePendingTaskCount } from '@/modules/task/hooks/usePendingTaskCount'
import { ShortcutGridSection } from '../components/sections/shortcut-grid-section/ShortcutGridSection'
import { TaskPreviewSection } from '../components/sections/task-preview-section/TaskPreviewSection'
import { UpcomingAppointmentSection } from '../components/sections/upcoming-appointment-section/UpcomingAppointmentSection'
import { useGetUpcomingAppointment } from '../hooks/useGetUpcomingAppointment'

import type { TaskResponse } from '@/modules/task/types/task.types'

const HomePage = () => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const subjectMember = useSubjectMember()
  const { firstLoginState, setFirstLoginState } = useFirstLogin()
  const { pendingTaskCount } = usePendingTaskCount()
  // Defer the fetch until subjectMember is available — passing null skips the
  // request entirely, preventing a premature call with subjectId=undefined.
  const { data: tasks, isLoading: tasksLoading } = useHealthblocksSWR<TaskResponse>(
    subjectMember?.id ? `/task?status=pending&page=0&limit=3&subjectId=${subjectMember.id}` : null,
  )

  const { data: upcomingAppointment, isLoading: appointmentLoading } = useGetUpcomingAppointment()

  // const { data: unreadNotificationState, isLoading: notificationsLoading } =
  //   useGetNotificationsPreview()

  const theme = useTheme()

  // Template variables
  // ------------------------------------------------------------------------- /
  // Also show the loader while subjectMember is resolving, since the task
  // fetch is deferred until the member is ready.
  const isLoading = !subjectMember || tasksLoading || appointmentLoading
  // || notificationsLoading

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (!firstLoginState) {
      mixpanel.track('account_created')
      setFirstLoginState(true)
    }
  }, [firstLoginState, setFirstLoginState])

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <ContentPageLayout
      backgroundColor={theme.Colors.Background.Light}
      noPadding
      header={
        <Suspense fallback={null}>
          <NativePlatform>
            <StMobileHeader>
              <AppHeader
                enableNotifications
                profileButtonEnabled
                centered={false}
                title={i18n._(globalDefinitions.greeting, {
                  value: subjectMember?.givenName,
                })}
              />
              <HospitalIndicator variant='header' />
            </StMobileHeader>
          </NativePlatform>
        </Suspense>
      }
    >
      <AnimatePresence>
        {isLoading ? (
          <AnimatedLoader />
        ) : (
          <StContainer>
            <FlexColumn justifyContent='space-between' gap={theme.UI.SpacingPx.Space10} style={{ gridArea: 'content' }}>
              <AppUpdateCard key='enroll-appUpdate-card' data-test-id='home-app-update-card' />
              <NotificationsCard key='enroll-noti-card' data-test-id='home-notifications-card' />

              <TaskPreviewSection key='task-preview' tasks={tasks?.data} numberOfTasks={pendingTaskCount} />

              <UpcomingAppointmentSection upcomingAppointment={upcomingAppointment} />

              <ShortcutGridSection key='shortcut-grid' />
            </FlexColumn>

            {/* <div style={{ gridArea: 'notifications' }}> */}
            {/* <NotificationPreviewSection
                unreadNotificationState={unreadNotificationState}
              /> */}
            {/* </div> */}
          </StContainer>
        )}
      </AnimatePresence>
      <CareQuestionMobileActionButton />
    </ContentPageLayout>
  )
}

const StMobileHeader = styled.div`
  ${ContentPagePaddingCSS}

  padding-bottom: 0;
`

const StContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space10};

  /* Remove margin or gap for empty sections */
  > *:empty {
    margin: 0;
    padding: 0;
    display: none;
  }

  ${ContentPagePaddingCSS}

  padding-bottom: calc(
    ${({ theme }) => theme.UI.SpacingPx.Space5} + 50px +
      var(--safe-area-inset-bottom, 0px)
  );

  @media ${breakpoints.m} {
    padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space8};
  }

  @media ${breakpoints.fromL} {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: ${({ theme }) => theme.UI.SpacingPx.Space8};
    width: 100%;

    padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space12};

    * {
      min-width: 0;
      max-width: 100%;
    }

    grid-template-columns: 70% 30%;

    grid-template-areas: 'content notifications';
  }
`

export { HomePage }
